import {
  SiteSubscription,
  SubscriptionPlanId,
  PlanQuotaConfig,
  SubscriptionStatus,
} from '../types';
import { SUBSCRIPTION_PLANS } from '../config/constants';
import { realtimeDbService as dbService } from './realtimeDbService';

export const PLAN_QUOTAS: Record<string, PlanQuotaConfig> = {
  PLAN_CORP_ANNUAL: {
    id: 'PLAN_CORP_ANNUAL',
    name: 'Kurumsal Web Sitesi Paketi',
    priceTRY: 15000,
    websiteLimit: 1,
    maxSites: 1,
    isWebsiteSubscription: true,
    maxStorageBytes: 10 * 1024 * 1024 * 1024, // 10 GB
    maxPages: 15,
    maxBlogs: 50,
    maxYachts: 0,
    maxMediaAssets: 200,
    allowCustomDomain: true,
    allowSeoManagement: true,
  },
  PLAN_CORPORATE: {
    id: 'PLAN_CORPORATE',
    name: 'Kurumsal Web Sitesi Paketi',
    priceTRY: 15000,
    websiteLimit: 1,
    maxSites: 1,
    isWebsiteSubscription: true,
    maxStorageBytes: 10 * 1024 * 1024 * 1024,
    maxPages: 15,
    maxBlogs: 50,
    maxYachts: 0,
    maxMediaAssets: 200,
    allowCustomDomain: true,
    allowSeoManagement: true,
  },
  CORPORATE_WEBSITE: {
    id: 'CORPORATE_WEBSITE',
    name: 'Kurumsal Web Sitesi Paketi',
    priceTRY: 15000,
    websiteLimit: 1,
    maxSites: 1,
    isWebsiteSubscription: true,
    maxStorageBytes: 10 * 1024 * 1024 * 1024,
    maxPages: 15,
    maxBlogs: 50,
    maxYachts: 0,
    maxMediaAssets: 200,
    allowCustomDomain: true,
    allowSeoManagement: true,
  },
  PLAN_YACHT_ANNUAL: {
    id: 'PLAN_YACHT_ANNUAL',
    name: 'Lüks Yat Kiralama Web Sitesi Paketi',
    priceTRY: 36000,
    websiteLimit: 1,
    maxSites: 1,
    isWebsiteSubscription: true,
    maxStorageBytes: 25 * 1024 * 1024 * 1024, // 25 GB
    maxPages: 30,
    maxBlogs: 100,
    maxYachts: 50,
    maxMediaAssets: 500,
    allowCustomDomain: true,
    allowSeoManagement: true,
  },
  PLAN_YACHT: {
    id: 'PLAN_YACHT',
    name: 'Lüks Yat Kiralama Web Sitesi Paketi',
    priceTRY: 36000,
    websiteLimit: 1,
    maxSites: 1,
    isWebsiteSubscription: true,
    maxStorageBytes: 25 * 1024 * 1024 * 1024,
    maxPages: 30,
    maxBlogs: 100,
    maxYachts: 50,
    maxMediaAssets: 500,
    allowCustomDomain: true,
    allowSeoManagement: true,
  },
  YACHT_RENTAL_WEBSITE: {
    id: 'YACHT_RENTAL_WEBSITE',
    name: 'Lüks Yat Kiralama Web Sitesi Paketi',
    priceTRY: 36000,
    websiteLimit: 1,
    maxSites: 1,
    isWebsiteSubscription: true,
    maxStorageBytes: 25 * 1024 * 1024 * 1024,
    maxPages: 30,
    maxBlogs: 100,
    maxYachts: 50,
    maxMediaAssets: 500,
    allowCustomDomain: true,
    allowSeoManagement: true,
  },
};

export class SubscriptionService {
  /**
   * Helper to check if a plan ID belongs to a website subscription
   */
  public static isWebsiteSubscription(planId?: string): boolean {
    if (!planId) return false;
    const p = String(planId).toUpperCase();
    if (p.includes('GOOGLE_ADS') || p.includes('AI_') || p.includes('VISIBILITY')) return false;
    return (
      p.includes('CORP') ||
      p.includes('YACHT') ||
      p === 'CORPORATE_WEBSITE' ||
      p === 'YACHT_RENTAL_WEBSITE'
    );
  }
  /**
   * Evaluates if a given subscription is active and not expired
   */
  public static isActive(subscription?: SiteSubscription | null): boolean {
    if (!subscription) return false;
    if (subscription.status === 'EXPIRED') return false;
    const now = Date.now();
    return subscription.expireDate > now;
  }

  /**
   * Calculates remaining days until expiration
   */
  public static getRemainingDays(expireDate: number): number {
    const now = Date.now();
    const msRemaining = expireDate - now;
    return Math.max(0, Math.ceil(msRemaining / (1000 * 60 * 60 * 24)));
  }

  /**
   * Retrieves plan quota configuration
   */
  public static getPlanQuotas(planId?: string): PlanQuotaConfig {
    if (planId && PLAN_QUOTAS[planId]) {
      return PLAN_QUOTAS[planId];
    }
    return PLAN_QUOTAS['PLAN_CORP_ANNUAL'];
  }

  /**
   * Verifies if a site has active publishing permissions
   */
  public static async canPublish(siteId: string, publishingUserId?: string): Promise<{ allowed: boolean; reason?: string }> {
    const site = await dbService.getSiteById(siteId);
    if (!site) {
      return { allowed: false, reason: 'Site bulunamadı.' };
    }

    if (site.status === 'SUSPENDED') {
      return { allowed: false, reason: 'Site yönetici tarafından askıya alınmıştır.' };
    }

    // 1. Admin bypass: Admins and system owners always have full publishing access
    const ADMIN_EMAILS = ['admin@adiosoft.com'];
    
    // Check candidate UIDs for admin privilege (publishingUserId, site.ownerId, or current session)
    const candidateUids = [publishingUserId, site.ownerId].filter(Boolean) as string[];

    for (const uid of candidateUids) {
      if (
        uid === 'admin_user' ||
        uid === 'system' ||
        uid === 'zrL9FtQF42aSt7cNDDVrzBAYOEP2'
      ) {
        return { allowed: true };
      }

      try {
        const user = await dbService.getUser(uid);
        if (
          user?.role === 'admin' ||
          user?.role === 'super_admin' ||
          (user as any)?.role === 'SUPER_ADMIN' ||
          (user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase()))
        ) {
          return { allowed: true };
        }
      } catch (err) {
        // continue
      }
    }

    // Check localStorage session for admin role
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const storedUser = localStorage.getItem('adiosoft_current_user_data');
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          if (
            parsed?.role === 'admin' ||
            parsed?.role === 'super_admin' ||
            parsed?.role === 'SUPER_ADMIN' ||
            (parsed?.email && ADMIN_EMAILS.includes(String(parsed.email).toLowerCase()))
          ) {
            return { allowed: true };
          }
        }
      }
    } catch {
      // ignore
    }

    if (site.ownerId) {
      // 2. Check if owner has an active user subscription or sector quota
      try {
        const userSubs = await dbService.getUserSubscriptions(site.ownerId);
        const targetSector = site.industry === 'YACHT_RENTAL' ? 'YACHT_RENTAL' : 'CORPORATE';
        const activeSectorSub = userSubs.find(
          (s) =>
            s.sector === targetSector &&
            s.status === 'ACTIVE' &&
            (!s.expireDate || s.expireDate > Date.now())
        );

        if (activeSectorSub) {
          // Sync active user subscription to site subscription
          const currentSiteSub = (await dbService.getSiteSubscription(siteId)) || site.subscription;
          if (!currentSiteSub || (currentSiteSub.expireDate && currentSiteSub.expireDate <= Date.now())) {
            const updatedSub: SiteSubscription = {
              siteId,
              planId: activeSectorSub.planId || (targetSector === 'YACHT_RENTAL' ? 'PLAN_YACHT_ANNUAL' : 'PLAN_CORP_ANNUAL'),
              planName: activeSectorSub.planName || (targetSector === 'YACHT_RENTAL' ? 'Yat Kiralama Paketi' : 'Kurumsal Web Sitesi Paketi'),
              priceTRY: activeSectorSub.priceTRY || 15000,
              amount: activeSectorSub.priceTRY || 15000,
              currency: 'TRY',
              websiteLimit: Number(activeSectorSub.websiteLimit) || 1,
              status: 'ACTIVE',
              startDate: activeSectorSub.startDate || Date.now(),
              expireDate: activeSectorSub.expireDate || (Date.now() + 365 * 86400000),
              autoRenew: true,
              updatedAt: Date.now(),
            };
            await dbService.saveSiteSubscription(siteId, updatedSub);
            site.subscription = updatedSub;
            await dbService.saveSite(site);
          }
          return { allowed: true };
        }
      } catch (err) {
        console.warn('SubscriptionService sector check warning:', err);
      }
    }

    const sub = (await dbService.getSiteSubscription(siteId)) || site.subscription;
    if (sub && sub.expireDate && sub.expireDate < Date.now()) {
      return {
        allowed: false,
        reason: 'Abonelik süreniz dolmuştur. Yayınlama yapabilmek için lütfen paketinizi yenileyiniz.',
      };
    }

    return { allowed: true };
  }

  /**
   * Validates whether a resource addition is within plan limits
   */
  public static async checkResourceQuota(
    siteId: string,
    resource: 'pages' | 'blogs' | 'yachts' | 'media' | 'storage',
    currentCount: number,
    incomingBytes = 0
  ): Promise<{ allowed: boolean; limit: number; current: number; reason?: string }> {
    const site = await dbService.getSiteById(siteId);
    const planId = site?.subscription?.planId || 'PLAN_CORP_ANNUAL';
    const quotas = this.getPlanQuotas(planId);

    switch (resource) {
      case 'pages':
        if (currentCount >= quotas.maxPages) {
          return {
            allowed: false,
            limit: quotas.maxPages,
            current: currentCount,
            reason: `Paketiniz en fazla ${quotas.maxPages} sayfa oluşturmanıza izin vermektedir.`,
          };
        }
        return { allowed: true, limit: quotas.maxPages, current: currentCount };

      case 'blogs':
        if (currentCount >= quotas.maxBlogs) {
          return {
            allowed: false,
            limit: quotas.maxBlogs,
            current: currentCount,
            reason: `Paketiniz en fazla ${quotas.maxBlogs} blog yazısı yayınlamanıza izin vermektedir.`,
          };
        }
        return { allowed: true, limit: quotas.maxBlogs, current: currentCount };

      case 'yachts':
        if (quotas.maxYachts === 0) {
          return {
            allowed: false,
            limit: 0,
            current: currentCount,
            reason: 'Yat kiralama filosu yönetimi yalnızca Yat Kiralama Paketi abonelerine açıktır.',
          };
        }
        if (currentCount >= quotas.maxYachts) {
          return {
            allowed: false,
            limit: quotas.maxYachts,
            current: currentCount,
            reason: `Paketiniz en fazla ${quotas.maxYachts} yat eklemenize izin vermektedir.`,
          };
        }
        return { allowed: true, limit: quotas.maxYachts, current: currentCount };

      case 'storage':
        const currentBytes = (site?.storageUsedMB || 0) * 1024 * 1024;
        const totalStorage = currentBytes + incomingBytes;
        if (totalStorage > quotas.maxStorageBytes) {
          const limitMb = Math.round(quotas.maxStorageBytes / (1024 * 1024));
          return {
            allowed: false,
            limit: quotas.maxStorageBytes,
            current: totalStorage,
            reason: `Depolama alanınız (${limitMb} MB) dolmuştur. Yeni görsel yüklemek için eski medyaları temizleyiniz.`,
          };
        }
        return { allowed: true, limit: quotas.maxStorageBytes, current: totalStorage };

      default:
        return { allowed: true, limit: 100, current: currentCount };
    }
  }

  /**
   * Validates whether a user can create a new website based on active subscriptions and websiteLimit.
   * Enforces quota at backend/database level using central QuotaService normalization.
   */
  public static async checkWebsiteQuota(
    userId: string,
    isSuperAdmin: boolean = false,
    targetSector?: 'CORPORATE' | 'YACHT_RENTAL'
  ): Promise<{
    allowed: boolean;
    limit: number;
    current: number;
    remaining: number;
    reason?: string;
    hasActiveSubscription: boolean;
  }> {
    const quotas = await dbService.getSectorQuotas(userId, isSuperAdmin);

    // If a specific sector is requested, evaluate that sector directly
    if (targetSector) {
      const sectorQuota = quotas[targetSector];
      const isAllowed = sectorQuota.allowed && sectorQuota.remaining > 0;
      return {
        allowed: isAllowed,
        limit: sectorQuota.total,
        current: sectorQuota.used,
        remaining: sectorQuota.remaining,
        hasActiveSubscription: sectorQuota.allowed,
        reason: !sectorQuota.allowed
          ? `${sectorQuota.title} için aktif bir website paketiniz bulunmuyor.`
          : sectorQuota.remaining <= 0
          ? `${sectorQuota.title} paketinizdeki website oluşturma hakkınız doldu (${sectorQuota.used} / ${sectorQuota.total}).`
          : undefined,
      };
    }

    // Otherwise evaluate overall platform quota
    const totalLimit = quotas.CORPORATE.total + quotas.YACHT_RENTAL.total;
    const totalUsed = quotas.CORPORATE.used + quotas.YACHT_RENTAL.used;
    const totalRemaining = quotas.CORPORATE.remaining + quotas.YACHT_RENTAL.remaining;
    const hasAnyActive = quotas.CORPORATE.allowed || quotas.YACHT_RENTAL.allowed;

    if (!hasAnyActive || totalLimit === 0) {
      return {
        allowed: false,
        limit: 0,
        current: totalUsed,
        remaining: 0,
        hasActiveSubscription: false,
        reason: 'Aktif bir web sitesi paketiniz bulunmuyor. Yeni bir site oluşturmak için lütfen sektör paketi seçiniz.',
      };
    }

    if (totalRemaining <= 0) {
      return {
        allowed: false,
        limit: totalLimit,
        current: totalUsed,
        remaining: 0,
        hasActiveSubscription: true,
        reason: `Mevcut paketlerinizdeki toplam website hakkınız doldu (${totalUsed} / ${totalLimit}).`,
      };
    }

    return {
      allowed: true,
      limit: totalLimit,
      current: totalUsed,
      remaining: totalRemaining,
      hasActiveSubscription: true,
    };
  }

  /**
   * Retrieves isolated sector quotas using dbService.getSectorQuotas
   */
  public static async getSectorQuotas(userId: string, isSuperAdmin: boolean = false) {
    return dbService.getSectorQuotas(userId, isSuperAdmin);
  }

  /**
   * Validates whether a user can create a website specifically in the given sector.
   */
  public static async checkSectorWebsiteQuota(
    userId: string,
    sector: 'CORPORATE' | 'YACHT_RENTAL',
    isSuperAdmin: boolean = false
  ): Promise<{
    allowed: boolean;
    limit: number;
    current: number;
    remaining: number;
    reason?: string;
  }> {
    const quotas = await dbService.getSectorQuotas(userId, isSuperAdmin);
    const sectorQuota = quotas[sector];

    if (!sectorQuota.allowed || sectorQuota.total === 0) {
      return {
        allowed: false,
        limit: sectorQuota.total,
        current: sectorQuota.used,
        remaining: 0,
        reason: `${sectorQuota.title} için aktif bir website paketiniz bulunmuyor.`,
      };
    }

    if (sectorQuota.remaining <= 0 || sectorQuota.used >= sectorQuota.total) {
      return {
        allowed: false,
        limit: sectorQuota.total,
        current: sectorQuota.used,
        remaining: 0,
        reason: `${sectorQuota.title} paketinizdeki website oluşturma hakkınız doldu (${sectorQuota.used} / ${sectorQuota.total}).`,
      };
    }

    return {
      allowed: true,
      limit: sectorQuota.total,
      current: sectorQuota.used,
      remaining: sectorQuota.remaining,
    };
  }
}
